import {Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpHeaders} from '@angular/common/http';
import {Observable, timeout} from 'rxjs';

import {environment} from '../../../environments/environment';
import {skipForbiddenRedirect} from '../../interceptors/forbidden-redirect.context';
import {LocalStorageService} from '../local-storage.service';

/*
 * Tipi locali del contratto «Documenti per comune»: tenuti locali APPOSTA, non sostituiti dal client
 * generato. Hanno campi obbligatori più stretti di quelli (tutti opzionali) del generato e le date
 * (`LocalDateTime` UTC senza zona) si leggono con `parseBackendLocalDateTime`. I nomi snake_case
 * combaciano con i modelli generati (`DocumentoCittadinoDTO`, `DocumentoVigenteDTO`,
 * `DocumentiDaAccettareDTO`, `DocumentiAccettazioneForm`). La chiamata resta su `HttpClient` diretto.
 */

/** Tipo di spunta: ACCETTO e HO_LETTO sono obbligatorie, ACCONSENTO è facoltativa. */
export type TipoSpuntaDocumento = 'ACCETTO' | 'HO_LETTO' | 'ACCONSENTO';

export interface DocumentoSpuntaCittadino {
  id: string;
  tipo: TipoSpuntaDocumento;
  obbligatoria: boolean;
  testo: string;
  ordine: number;
}

export interface DocumentoCittadino {
  documento_id: string;
  versione: number;
  /** Data di pubblicazione della versione: `LocalDateTime` UTC senza zona (es. `2026-10-23T09:28:10.123`). */
  pubblicato_at: string;
  titolo: string;
  testo_html: string;
  /** Il cittadino aveva già accettato una versione precedente di questo documento. */
  aggiornamento: boolean;
  spunte: DocumentoSpuntaCittadino[];
}

export interface DocumentiDaAccettare {
  da_accettare: boolean;
  documenti: DocumentoCittadino[];
}

export interface AccettazioneSpunta {
  spunta_id: string;
  valore: boolean;
}

export interface MiaAccettazione {
  versione: number;
  accettato_at: string;
  spunte: AccettazioneSpunta[];
}

export interface DocumentoVigente extends DocumentoCittadino {
  mia_accettazione: MiaAccettazione | null;
}

export interface DocumentiAccettazioneForm {
  documenti: {
    documento_id: string;
    versione: number;
    spunte: AccettazioneSpunta[];
  }[];
}

/** Chiave dell'errore 422 «la versione è cambiata mentre il cittadino leggeva». */
export const DOCUMENTO_VERSIONE_NON_CORRENTE_KEY = 'documento_versione_non_corrente';

/** Oltre questo tempo la chiamata è considerata fallita: il gate lascia passare (fail-open). */
export const DOCUMENTI_COMUNE_TIMEOUT_MS = 8_000;

/**
 * Client HTTP diretto per `/api/v1/cittadino/comuni/{slug}/documenti`.
 *
 * Bearer aggiunto a mano (come gli altri servizi su `HttpClient`); `Accept-Language` lo mette
 * `AcceptLanguageInterceptor`. I documenti sono solo in italiano: il backend non restituisce la lingua.
 * `SKIP_FORBIDDEN_REDIRECT`: un 403 non deve riportare a `/pages/home` per colpa del gate, che
 * in caso di errore semplicemente non blocca.
 */
@Injectable({providedIn: 'root'})
export class DocumentiComuneApi {
  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
  ) {}

  /** Documenti ATTIVI con una versione che il cittadino non ha ancora accettato. */
  daAccettare$(slug: string): Observable<DocumentiDaAccettare> {
    return this.http
      .get<DocumentiDaAccettare>(this.url(slug, '/da-accettare'), this.opzioni())
      .pipe(timeout(DOCUMENTI_COMUNE_TIMEOUT_MS));
  }

  /** Documenti vigenti con l'ultima accettazione del cittadino (consultazione). */
  vigenti$(slug: string): Observable<DocumentoVigente[]> {
    return this.http
      .get<DocumentoVigente[]>(this.url(slug, ''), this.opzioni())
      .pipe(timeout(DOCUMENTI_COMUNE_TIMEOUT_MS));
  }

  /** Registra l'accettazione (tutto o niente). Risponde con lo stato aggiornato. */
  accetta$(slug: string, form: DocumentiAccettazioneForm): Observable<DocumentiDaAccettare> {
    return this.http
      .post<DocumentiDaAccettare>(this.url(slug, '/accettazioni'), form, this.opzioni())
      .pipe(timeout(DOCUMENTI_COMUNE_TIMEOUT_MS));
  }

  private url(slug: string, suffisso: string): string {
    return `${environment.servicesUrl}/api/v1/cittadino/comuni/${encodeURIComponent(slug)}/documenti${suffisso}`;
  }

  private opzioni(): {headers: HttpHeaders; context: HttpContext} {
    let headers = new HttpHeaders().set('Accept', 'application/json');
    const token = this.localStorageService.getAccessToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return {headers, context: skipForbiddenRedirect()};
  }
}
